import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { AUTH_API_BASE_URL, AuthStateService } from '@pressteaze/auth';
import { firstValueFrom } from 'rxjs';

interface OperatorAccessTokenResponse {
	readonly access_token: string;
	readonly expires_in: number;
}

interface CachedOperatorAccessToken {
	readonly operatorSessionToken: string;
	readonly accessToken: string;
	readonly refreshAt: number;
}

@Injectable({ providedIn: 'root' })
export class OperatorAccessTokenProvider {
	private readonly http = inject(HttpClient);
	private readonly baseUrl = inject(AUTH_API_BASE_URL);
	private readonly authState = inject(AuthStateService);
	private cached?: CachedOperatorAccessToken;
	private pending?: {
		readonly operatorSessionToken: string;
		readonly request: Promise<string>;
	};

	async getAccessToken(): Promise<string | null> {
		const operatorSessionToken = this.authState.operatorToken();
		if (!operatorSessionToken) {
			this.clear();
			return null;
		}

		if (
			this.cached?.operatorSessionToken === operatorSessionToken &&
			Date.now() < this.cached.refreshAt
		) {
			return this.cached.accessToken;
		}

		if (this.pending?.operatorSessionToken === operatorSessionToken) {
			return this.pending.request;
		}

		const request = this.issueAccessToken(operatorSessionToken);
		this.pending = { operatorSessionToken, request };
		try {
			return await request;
		} finally {
			if (this.pending?.request === request) this.pending = undefined;
		}
	}

	clear(): void {
		this.cached = undefined;
		this.pending = undefined;
	}

	private async issueAccessToken(operatorSessionToken: string): Promise<string> {
		const response = await firstValueFrom(
			this.http.post<OperatorAccessTokenResponse>(
				`${this.baseUrl}/operator-realtime-token`,
				{},
				{
					headers: new HttpHeaders({
						Authorization: `Bearer ${operatorSessionToken}`,
					}),
				},
			),
		);
		const refreshLeewaySeconds = Math.min(60, response.expires_in / 2);
		this.cached = {
			operatorSessionToken,
			accessToken: response.access_token,
			refreshAt:
				Date.now() +
				Math.max(0, response.expires_in - refreshLeewaySeconds) * 1000,
		};
		return response.access_token;
	}
}
